import { Location } from '@angular/common';
import { Component, AfterViewChecked, ElementRef, ViewChild, HostListener, NgZone, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { ErrorDialogComponent } from 'src/app/error-dialog/error-dialog.component';
import { ApiService } from 'src/app/services/api.service';
import { CommonService } from 'src/app/services/common.service';
import { CookieService } from 'ngx-cookie-service';

interface MessageResponsePair {
  message: string;
  timestamp: string;
  response?: string[];
  completeResponse?: any;
  loading?: boolean;
  currentPage?: number;
  totalPages?: number;
  isLiked?: boolean;
  isDisliked?: boolean;
}

@Component({
  selector: 'app-product-queries',
  templateUrl: './product-queries.component.html',
  styleUrls: ['./product-queries.component.css']
})
export class ProductQueriesComponent implements AfterViewChecked, OnDestroy {
  inputValue: string = '';
  translatedText: string = '';
  isListening: boolean = false;
  isProcessing: boolean = false;
  voiceError: string = '';
  recognition: any;
  processedResults: Set<string> = new Set();
  debounceTimeout: any = null;
  combinedTranscript: string = '';
  isVoiceEnabled = true;
  mediaRecorder: any;
  audioChunks: BlobPart[] = [];
  audioBlob!: Blob;
  currentYear: number = new Date().getFullYear();
  inputText: string = '';
  messageResponsePairs: MessageResponsePair[] = [];
  private subscription!: Subscription;
  pointersLiked: boolean = false;
  pointersDisliked: boolean = false;
  isDropdownOpen: boolean = false;
  display: boolean = false;
  missingUpdate: boolean = false;
  incompleteInfo: boolean = false;
  needsImprovement: boolean = false;
  incorrectResponse: boolean = false;
  cantUnderstand: boolean = false;
  others: boolean = false;
  selectedReasonsArray: string[] = [];
  selectedPairForDislike: any;
  selectedPairIndexForDislike!: number;
  feedback: string = '';
  isResponseLoading: boolean = false;
  selectedProduct: any;
  @ViewChild('chatContainer') private chatContainer!: ElementRef;
  apiError: boolean = false;
  history: any[] = [];
  $historySubscription: Subscription;
  $voiceSubscription: Subscription;
  isProcessLoading: boolean = false;
  
  // Confirmation Popup State
  showProcessChangeConfirm: boolean = false;
  pendingProcessSelection: string | null = null;

  // New UI Variables
  isProcessMenuOpen: boolean = false;
  processSearchQuery: string = '';
  availableProcesses: string[] = [];
  showHowToUseModal: boolean = false;
  showQuickTourModal: boolean = false;

  // Session management variables
  turnCount: number = 0;
  showSessionExpiredModal: boolean = false;

  suggestedItems: string[] = [];
  BANK: any = {
    'house wife': [
      'What are the gate criteria for a house wife applying for insurance?',
      'What documents are required for a house wife applicant?',
      'Is income proof required for a house wife?',
      'What are the eligibility criteria for a house wife?',
      'What are the gate criteria for a student applying for insurance?'
    ],
    'student': [
      'What are the gate criteria for a student applying for insurance?',
      'What documents are required for a student applicant?',
      'Is income proof required for a student?',
      'What is the maximum sum assured for a student?'
    ],
    'income': [
      'What income documents are required?',
      'What is the minimum income criterion?',
      'Is income proof required for a house wife?'
    ],
    'medical': [
      'What medical tests are required for senior citizens?',
      'When is a tele-MER triggered?',
      'What conditions need a full medical?'
    ],
    'pincode': [
      'Is this pincode serviceable?',
      'Which pincodes are restricted in Maharashtra?'
    ]
  };

  // Focus Area State
  selectedFocusArea: string = '';

  constructor(
    public commonService: CommonService, 
    private apiService: ApiService,
    private dialogService: DialogService, 
    private router: Router,
    private ngZone: NgZone, 
    private cdRef: ChangeDetectorRef, 
    private cookie: CookieService
  ) {
    this.commonService.isVoiceModeEnabled.next(true);
    this.commonService.initializeSession();
    this.isProcessLoading = true;
    
    const accessToken = this.cookie.get('access_token');
    if (accessToken) {
      // Reverted to getUserProcess() for existing employees
      this.apiService.getUserProcess().subscribe({
        next: (res: any) => {
          if (res.available_processes && Array.isArray(res.available_processes) && res.available_processes.length > 0) {
            this.availableProcesses = res.available_processes;
          } else if (res.process_name) {
            this.availableProcesses = [res.process_name];
          } else {
            this.availableProcesses = ['Process']; 
          }

          const defaultProcess = this.availableProcesses.length > 0 ? this.availableProcesses[0] : "Process";

          let selectedProduct = {
            productType: 'underwriting',
            product: defaultProcess,
            moduleType: 'product-queries',
            company: "Process",
            process_name: defaultProcess,
            sub_category: null
          };

          this.commonService.setProduct(selectedProduct);
          this.selectedProduct = selectedProduct;
          this.isProcessLoading = false;
        },
        error: (err) => {
          // The global interceptor handles 401/403, but routing fallback kept for safety
          if (err.status === 401 || err.status === 403) {
            this.router.navigate(['/login']);
          } else {
            this.apiError = true;
          }
          this.isProcessLoading = false;
        }
      });
    } else {
      router.navigate(['/login']);
    }

    this.$historySubscription = this.commonService.history.subscribe((res) => {
      this.history = res;
    });
    this.$voiceSubscription = commonService.isVoiceModeEnabled.subscribe((res) => {
      this.isVoiceEnabled = res;
    });
  }

  // Dropdown Logic
  get filteredProcesses() {
    if (!this.processSearchQuery) return this.availableProcesses;
    return this.availableProcesses.filter(p => p.toLowerCase().includes(this.processSearchQuery.toLowerCase()));
  }

  toggleProcessMenu(event: Event) {
    event.stopPropagation();
    this.isProcessMenuOpen = !this.isProcessMenuOpen;

    if (this.isProcessMenuOpen) {
      this.processSearchQuery = '';

      setTimeout(() => {
        const container = document.querySelector('.options-container') as HTMLElement;
        const selectedItem = document.querySelector('.proc-menu .opt[aria-selected="true"]') as HTMLElement;

        if (container && selectedItem) {
          selectedItem.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        }
      }, 0);
    }
  }

  selectProcess(proc: string) {
    if (this.selectedProduct) {
      if (this.selectedProduct.product !== proc) {
        if (this.messageResponsePairs && this.messageResponsePairs.length > 0) {
          this.pendingProcessSelection = proc;
          this.showProcessChangeConfirm = true;
        } else {
          this.selectedProduct.product = proc;
          this.selectedProduct.company = proc;
          this.selectedProduct.process_name = proc;
        }
        this.isProcessMenuOpen = false;
      } else {
        this.isProcessMenuOpen = false;
      }
    }
  }

  confirmProcessChange() {
    if (this.pendingProcessSelection) {
      const proc = this.pendingProcessSelection;
      this.selectedProduct.product = proc;
      this.selectedProduct.company = proc;
      this.selectedProduct.process_name = proc;
      this.messageResponsePairs = [];
      this.turnCount = 0; 
      this.commonService.initializeSession();
    }
    this.showProcessChangeConfirm = false;
    this.pendingProcessSelection = null;
  }

  cancelProcessChange() {
    this.showProcessChangeConfirm = false;
    this.pendingProcessSelection = null;
  }

  // Suggestion/Autocomplete Logic
  onTextInput(event: Event) {
    const target = event.target as HTMLTextAreaElement;
    this.adjustTextareaHeight(target);

    const q = this.inputText.trim().toLowerCase();
    if (q.length < 2) {
      this.suggestedItems = [];
      return;
    }

    let found = false;
    for (const key in this.BANK) {
      if (q.includes(key)) {
        this.suggestedItems = this.BANK[key];
        found = true;
        break;
      }
    }

    if (!found) {
      const all: string[] = [].concat.apply([], Object.values(this.BANK));
      this.suggestedItems = all.filter(s => s.toLowerCase().includes(q)).slice(0, 5);
    }
  }

  selectSuggestion(suggestion: string) {
    this.inputText = suggestion;
    this.suggestedItems = [];
    setTimeout(() => {
      const ta = document.getElementById('input') as HTMLTextAreaElement;
      if (ta) {
        ta.focus();
        this.adjustTextareaHeight(ta);
      }
    });
  }

  highlightText(text: string, term: string): string {
    if (!term) return this.escapeHtml(text);
    const escapedText = this.escapeHtml(text);
    const re = new RegExp('(' + term.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
    return escapedText.replace(re, '<mark style="background: transparent; color: #004b93; font-weight: 600;">$1</mark>');
  }

  private escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) => {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c as keyof typeof c] || c;
    });
  }

  adjustTextareaHeight(target: any) {
    const textarea = target as HTMLTextAreaElement;
    textarea.style.height = '55px'; 
    textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  sendMessage(textarea: HTMLTextAreaElement) {
    if (this.turnCount >= 10) {
      this.showSessionExpiredModal = true;
      return;
    }

    if (this.inputText.trim() !== '') {
      this.suggestedItems = []; 

      const newPair: MessageResponsePair = { message: this.inputText.trim(), timestamp: "", loading: true };
      this.messageResponsePairs.push(newPair);
      this.isResponseLoading = true;
      this.turnCount++; 

      setTimeout(() => {
        textarea.style.height = '55px';
      }, 0);

      let payLoad = {
        session_id: sessionStorage.getItem('sessionId'),
        query: this.inputText.trim(),
        focus_area: this.selectedFocusArea
      }

      this.subscription = this.apiService.getProductResponse(payLoad, "Process", this.selectedProduct.product, this.selectedProduct.productType).subscribe({
        next: (res) => {
          this.inputText = "";
          newPair.loading = false;
          newPair.response = (this.splitTextIntoChunks(this.formatTextToHTML(res.response), 150));
          newPair.completeResponse = res.response;
          newPair.timestamp = res.timestamp;
          newPair.totalPages = newPair.response.length;
          newPair.currentPage = 0;
          this.isResponseLoading = false;

          setTimeout(() => {
            textarea.style.height = '55px';
            document.getElementById('final')?.scrollIntoView({ behavior: "smooth" });
          }, 0);
        },
        error: (err) => {
          this.apiError = true;
          this.isResponseLoading = false;
          newPair.loading = false;
        }
      })
    }
  }

  resetSession() {
    this.showSessionExpiredModal = false;
    this.turnCount = 0;
    this.messageResponsePairs = [];
    this.inputText = '';
    this.commonService.initializeSession();
  }

  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  scrollToBottom(): void {
    try {
      this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
    } catch (err) { }
  }

  showDialog(pair: any, index: number) {
    this.selectedPairIndexForDislike = index;
    this.selectedPairForDislike = pair;
    this.display = true;
  }

  stopAPI() {
    if (this.subscription) this.subscription.unsubscribe();
    this.isResponseLoading = false;
    if (this.messageResponsePairs.length > 0) {
      this.messageResponsePairs[this.messageResponsePairs.length - 1].loading = false;
      if (!this.messageResponsePairs[this.messageResponsePairs.length - 1].response) {
        this.messageResponsePairs[this.messageResponsePairs.length - 1].response = [];
      }
    }
  }

  formatTextToHTML(text: string) {
    let formattedText = text.replace(/\n\n/g, '<br/><br/>');
    formattedText = formattedText.replace(/\n/g, '<br/>')
    formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
    return formattedText;
  }

  splitTextIntoChunks(text: string, wordLimit: number) {
    const sections = text.split(/(\*\*.*?\*\*|\n\n|<br\/>)/g);
    let chunks: string[] = [];
    let currentChunk = '';

    sections.forEach(section => {
      let tempChunk = currentChunk + section;
      if (tempChunk.split(' ').length > wordLimit) {
        if (section.includes('<b>')) {
          chunks.push(currentChunk.trim());
          currentChunk = section;
        } else {
          if (currentChunk) chunks.push(currentChunk.trim());
          currentChunk = section;
        }
      } else {
        currentChunk = tempChunk;
      }
    });
    if (currentChunk) chunks.push(currentChunk.trim());
    return chunks;
  }

  previousPage(index: number): void {
    if (this.messageResponsePairs[index].currentPage! > 0) {
      this.messageResponsePairs[index].currentPage!--;
    }
  }

  nextPage(index: number): void {
    if (this.messageResponsePairs[index].currentPage! < this.messageResponsePairs[index].totalPages! - 1) {
      this.messageResponsePairs[index].currentPage!++;
    }
  }

  getSelectedReasons() {
    this.selectedReasonsArray = [];
    if (this.missingUpdate) this.selectedReasonsArray.push('Missing critical update(s)');
    if (this.incompleteInfo) this.selectedReasonsArray.push('Incomplete information');
    if (this.needsImprovement) this.selectedReasonsArray.push('Needs improvement');
    if (this.incorrectResponse) this.selectedReasonsArray.push('Incorrect response');
    if (this.cantUnderstand) this.selectedReasonsArray.push("Can't understand");
    if (this.others) this.selectedReasonsArray.push('Others');
    return this.selectedReasonsArray.join(', ');
  }

  responseLiked(pair: any, index: number) {
    let payLoad = {
      session_id: sessionStorage.getItem("sessionId"),
      module: "underwriting_query",
      category: this.selectedProduct.productType,
      company: this.selectedProduct.company,
      product: this.selectedProduct.product,
      user_input: pair.message,
      response_content: pair.completeResponse,
      query_timestamp: pair.timestamp,
      feedback_type: "positive",
      feedback_category: "Accurate"
    }
    this.apiService.addFeedback(payLoad).subscribe({
      next: (res: any) => {
        this.messageResponsePairs[index].isLiked = true;
        this.messageResponsePairs[index].isDisliked = false;
      },
      error: (err: any) => {
        this.apiError = true;
      }
    })
  }

  responseDisLiked() {
    let pair = this.selectedPairForDislike;
    let index = this.selectedPairIndexForDislike;
    let payLoad = {
      session_id: sessionStorage.getItem("sessionId"),
      module: "underwriting_query",
      category: this.selectedProduct.productType,
      company: this.selectedProduct.company,
      product: this.selectedProduct.product,
      user_input: pair.message,
      response_content: pair.completeResponse,
      query_timestamp: pair.timestamp,
      feedback_type: "negative",
      feedback_category: this.getSelectedReasons(),
      feedback_text: this.feedback
    }
    this.apiService.addFeedback(payLoad).subscribe({
      next: (res: any) => {
        this.messageResponsePairs[index].isDisliked = true;
        this.messageResponsePairs[index].isLiked = false;
        this.display = false;
        this.cantUnderstand = false;
        this.incompleteInfo = false;
        this.incorrectResponse = false;
        this.needsImprovement = false;
        this.missingUpdate = false;
        this.others = false;
        this.feedback = "";
      },
      error: (err: any) => {
        this.apiError = true;
      }
    })
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    const clickedInsideKebab = target.closest('.kebab');
    if (!clickedInsideKebab && this.isDropdownOpen) {
      this.isDropdownOpen = false;
    }

    const clickedInsideProc = target.closest('.proc');
    if (!clickedInsideProc && this.isProcessMenuOpen) {
      this.isProcessMenuOpen = false;
    }

    const clickedInsideComposer = target.closest('.composer-wrap');
    if (!clickedInsideComposer && this.suggestedItems.length > 0) {
      this.suggestedItems = [];
    }
  }

  goBack() {
    let lastRoute = this.history[this.history.length - 1];
    this.router.navigate([lastRoute]);
  }

  ngOnDestroy() {
    this.$historySubscription.unsubscribe();
    this.$voiceSubscription.unsubscribe();
  }

  toggleVoiceRecognition() {
    if (!this.isListening) {
      this.startRecording();
    } else {
      this.stopRecording();
    }
  }

  startRecording() {
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        this.mediaRecorder = new MediaRecorder(stream);
        this.mediaRecorder.start();
        this.isListening = true;
        this.voiceError = '';
        this.audioChunks = [];

        this.mediaRecorder.ondataavailable = (event: any) => {
          this.audioChunks.push(event.data);
        };
      })
      .catch(error => {
        this.voiceError = 'Microphone access denied';
      });
  }

  stopRecording() {
    if (this.mediaRecorder) {
      this.mediaRecorder.stop();
      this.isListening = false;
      this.mediaRecorder.onstop = () => {
        this.audioBlob = new Blob(this.audioChunks, { type: 'audio/wav' });
        this.isProcessing = true;
        this.cdRef.detectChanges();
        this.sendAudioToApi(this.audioBlob);
      };
    }
  }

  sendAudioToApi(audioBlob: Blob) {
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.wav');
    formData.append('language_code', 'en');
    this.apiService.getTranscription(formData).subscribe((res) => {
      this.ngZone.run(() => {
        this.inputText += res.transcription;
        this.isProcessing = false;
        setTimeout(() => {
          const textarea = document.getElementById('input') as HTMLTextAreaElement;
          if (textarea) this.adjustTextareaHeight(textarea);
        });
        this.cdRef.detectChanges();
      });
    })
  }

  logout() {
    this.commonService.logout();
  }

  onKeyDown(event: KeyboardEvent, inputEl: HTMLTextAreaElement) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if ((this.inputText ?? '').trim().length === 0) return;
      this.sendMessage(inputEl);
    }
  }

  toggleFocusArea(area: string) {
    if (this.selectedFocusArea === area) {
      this.selectedFocusArea = '';
    } else {
      this.selectedFocusArea = area;
    }
  }
}